import React from 'react';
import { 
  CheckCircle, 
  ShieldCheck, 
  Zap, 
  ArrowRight, 
  FileSpreadsheet, 
  Layers, 
  RefreshCw,
  Lock,
  FileText,
  Clock,
  Sparkles
} from 'lucide-react';

interface IntegrationsShowcaseProps {
  onOpenDemo: () => void;
}

export const IntegrationsShowcase: React.FC<IntegrationsShowcaseProps> = ({ onOpenDemo }) => {
  const posSystems = [
    {
      name: 'Toast POS',
      badge: 'CSV Import Ready',
      status: 'Instant CSV Available Today',
      futureStatus: 'Direct API Sync in Future Rollout',
      features: 'Import server sales, credit card tips, and clock-in labor hours via standard Toast CSV reports.',
      color: 'from-amber-500/10 to-orange-500/10 border-orange-500/30 text-orange-600',
      logoText: 'TOAST'
    },
    {
      name: 'Square POS',
      badge: 'CSV Import Ready',
      status: 'Instant CSV Available Today',
      futureStatus: 'Direct API Sync in Future Rollout',
      features: 'Import shift gratuity pools and team clock-in timecards directly from Square shift summary CSVs.',
      color: 'from-blue-500/10 to-cyan-500/10 border-blue-500/30 text-blue-600',
      logoText: 'SQUARE'
    },
    {
      name: 'Clover POS',
      badge: 'CSV Import Ready',
      status: 'Instant CSV Available Today',
      futureStatus: 'Direct API Sync in Future Rollout',
      features: 'Import tip batch settlements and employee role mappings using pre-configured Clover CSV templates.',
      color: 'from-emerald-500/10 to-teal-500/10 border-emerald-500/30 text-emerald-600',
      logoText: 'CLOVER'
    }
  ];

  const payrollPartners = [
    {
      name: 'Gusto Payroll',
      badge: 'CSV Export Ready',
      status: 'Pre-Formatted Export Available Today',
      description: 'Auto-formatted tip income lines ready for Gusto bi-weekly imports without manual formula mapping.',
      logoText: 'GUSTO'
    },
    {
      name: 'ADP Workforce Now',
      badge: 'CSV Export Ready',
      status: 'Pre-Formatted Export Available Today',
      description: 'Pre-validated payroll CSV formats aligned with ADP pay code standards and overtime rules.',
      logoText: 'ADP'
    },
    {
      name: 'Paychex Flex',
      badge: 'CSV Export Ready',
      status: 'Pre-Formatted Export Available Today',
      description: 'IRS Form 8027 tip reporting codes and clean gross wages breakdown exports ready for upload.',
      logoText: 'PAYCHEX'
    }
  ];

  return (
    <section id="integrations" className="py-24 bg-white border-t border-b border-gray-150 relative overflow-hidden scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6">
        
        {/* Header */}
        <div className="text-center space-y-4 mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 border border-blue-100 rounded-full text-[10px] font-black uppercase tracking-widest text-blue-600">
            <Layers size={14} className="text-blue-600" />
            Seamless POS & Payroll Ecosystem
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-gray-900 tracking-tighter uppercase italic">
            CONNECTS WITH YOUR <span className="text-blue-600">EXISTING STACK</span>
          </h2>
          
          {/* Transparency Highlight Banner */}
          <div className="max-w-3xl mx-auto p-4 bg-gray-50 border border-gray-200 rounded-2xl">
            <p className="text-xs md:text-sm font-bold text-gray-800 leading-relaxed">
              <span className="text-blue-600 font-black uppercase">Seamless Multi-Format Integration:</span> Instant CSV & Spreadsheet Importer Available Today • Direct API Sync (Square, Toast, Clover) in Future Rollout
            </p>
          </div>
        </div>

        {/* Section 1: POS Integrations Grid */}
        <div className="mb-16">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="text-xs font-black uppercase tracking-widest text-gray-400 block">Supported POS Formats</span>
              <h3 className="text-xl font-black text-gray-900 uppercase italic">Point of Sale Systems (CSV Ready)</h3>
            </div>
            <button
              onClick={onOpenDemo}
              className="text-xs font-black uppercase tracking-widest text-blue-600 hover:text-blue-700 flex items-center gap-1.5 cursor-pointer self-start sm:self-auto"
            >
              <span>See POS Demo in Action</span>
              <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {posSystems.map((pos) => (
              <div 
                key={pos.name}
                className="p-6 rounded-3xl bg-gray-50/90 border border-gray-200 hover:bg-white hover:border-blue-300 hover:shadow-xl transition-all flex flex-col justify-between space-y-5 group"
              >
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-black text-sm tracking-wider px-3 py-1.5 rounded-xl bg-white border border-gray-200 text-gray-900 shadow-xs">
                      {pos.logoText}
                    </span>
                    <span className="text-[9px] font-black uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md flex items-center gap-1">
                      <CheckCircle size={10} className="text-emerald-600" />
                      {pos.badge}
                    </span>
                  </div>
                  <h4 className="font-black text-gray-900 text-lg">{pos.name}</h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    {pos.features}
                  </p>
                </div>
                
                <div className="pt-3 border-t border-gray-200 space-y-1.5">
                  <div className="flex items-center justify-between text-[10px] font-black text-emerald-700">
                    <span>{pos.status}</span>
                    <CheckCircle size={12} className="text-emerald-500" />
                  </div>
                  <div className="flex items-center gap-1.5 text-[9px] font-bold text-gray-400">
                    <Clock size={10} className="text-blue-500" />
                    <span>{pos.futureStatus}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Section 2: Payroll Integrations & Compliance Row */}
        <div className="bg-gray-900 rounded-[2.5rem] p-8 md:p-12 text-white space-y-8 relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 border-b border-gray-800 pb-6">
            <div>
              <div className="inline-flex items-center gap-2 text-emerald-400 text-xs font-black uppercase tracking-widest mb-1">
                <FileSpreadsheet size={16} />
                Payroll Export Engine
              </div>
              <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tight italic">
                Direct Payroll CSV Formatting
              </h3>
            </div>
            <p className="text-xs text-gray-400 max-w-md font-medium">
              Export pre-reconciled tip allocations with 1 click in the exact schema demanded by your payroll processor.
            </p>
          </div>

          {/* Payroll Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {payrollPartners.map((payroll) => (
              <div 
                key={payroll.name}
                className="bg-white/5 border border-white/10 p-6 rounded-2xl space-y-4 backdrop-blur-xs hover:border-white/20 transition-all flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="font-mono font-black text-sm text-white px-2.5 py-1 bg-white/10 rounded-lg border border-white/15">
                      {payroll.logoText}
                    </span>
                    <span className="text-[8px] font-black uppercase bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded border border-emerald-500/30">
                      {payroll.badge}
                    </span>
                  </div>
                  <h4 className="font-bold text-white text-base">{payroll.name}</h4>
                  <p className="text-xs text-gray-400 leading-relaxed font-medium">
                    {payroll.description}
                  </p>
                </div>
                
                <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-bold text-emerald-300">
                  <span>{payroll.status}</span>
                  <CheckCircle size={12} className="text-emerald-400" />
                </div>
              </div>
            ))}
          </div>

          {/* Compliance Safeguards & Best Practices Bar */}
          <div className="pt-6 border-t border-gray-800 flex flex-wrap items-center justify-between gap-4 text-xs font-bold text-gray-300">
            <div className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-blue-400" />
              <span>FLSA-Aligned Tip Pool Workflows</span>
            </div>
            <div className="flex items-center gap-2">
              <Lock size={18} className="text-purple-400" />
              <span>IRS Form 8027 8% Shortfall Calculation Tools</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={18} className="text-emerald-400" />
              <span>State Credit Card Deduction Safeguards (CA/MA/ME Protection Rules)</span>
            </div>
          </div>

        </div>

      </div>
    </section>
  );
};
